/**
 * Field Registry Configuration
 *
 * Purpose: Complete configuration for all 25 field types
 * Created: November 1, 2025
 *
 * This file contains the centralized metadata for all field types,
 * eliminating the need for hardcoded switch statements throughout the app.
 */

import {
  Type,
  Mail,
  Phone,
  Calendar,
  Hash,
  ToggleLeft,
  List,
  FileText,
  Globe,
  Square,
  MapPin,
  CreditCard,
  User,
  Building2,
  FolderOpen,
  Layout,
  Minus,
  MessageSquare,
  Lock,
  DollarSign,
  Link,
  Clock,
  CheckSquare,
  Circle,
  Percent,
  ArrowUpDown,
  FileJson,
  FileSearch,
} from 'lucide-react';

import type { FieldTypeConfig, UnifiedFieldType } from '@xanectech/types';

/**
 * Complete field type configurations
 * Maps each field type to its metadata and behavior
 */
export const fieldTypeConfigs: Record<UnifiedFieldType, FieldTypeConfig> = {
  // ========================================
  // DATA ENTRY FIELDS
  // ========================================

  text: {
    type: 'text',
    label: 'Text',
    description: 'Single line text input',
    category: 'data-entry',
    icon: Type,
    htmlInputType: 'text',
    componentPath: 'data-entry/TextField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'minLength',
        label: 'Minimum Length',
        type: 'number',
        required: false,
      },
      {
        key: 'maxLength',
        label: 'Maximum Length',
        type: 'number',
        required: false,
      },
      {
        key: 'pattern',
        label: 'Regex Pattern',
        type: 'text',
        required: false,
        helpText: 'Optional regular expression for validation',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  email: {
    type: 'email',
    label: 'Email',
    description: 'Email address with validation',
    category: 'data-entry',
    icon: Mail,
    htmlInputType: 'email',
    componentPath: 'data-entry/EmailField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    defaultValidationRules: {
      pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
      message: 'Please enter a valid email address',
    },
    configOptions: [
      {
        key: 'workEmailOnly',
        label: 'Work Email Only',
        type: 'boolean',
        required: false,
        defaultValue: false,
        helpText: 'Reject common personal email domains',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  phone: {
    type: 'phone',
    label: 'Phone',
    description: 'Phone number with country-specific formatting',
    category: 'data-entry',
    icon: Phone,
    htmlInputType: 'tel',
    componentPath: 'data-entry/PhoneField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'defaultCountry',
        label: 'Default Country Code',
        type: 'text',
        required: false,
        defaultValue: 'US',
        helpText: 'ISO country code for default formatting',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  password: {
    type: 'password',
    label: 'Password',
    description: 'Password input with show/hide toggle',
    category: 'data-entry',
    icon: Lock,
    htmlInputType: 'password',
    componentPath: 'data-entry/PasswordField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'minLength',
        label: 'Minimum Length',
        type: 'number',
        required: false,
        defaultValue: 8,
      },
      {
        key: 'requireComplexity',
        label: 'Require Complexity',
        type: 'boolean',
        required: false,
        defaultValue: true,
        helpText: 'Enforce password complexity rules',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  number: {
    type: 'number',
    label: 'Number',
    description: 'Numeric input with min/max validation',
    category: 'data-entry',
    icon: Hash,
    htmlInputType: 'number',
    componentPath: 'data-entry/NumberField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'min',
        label: 'Minimum Value',
        type: 'number',
        required: false,
      },
      {
        key: 'max',
        label: 'Maximum Value',
        type: 'number',
        required: false,
      },
      {
        key: 'step',
        label: 'Step',
        type: 'number',
        required: false,
        defaultValue: 1,
      },
      {
        key: 'decimals',
        label: 'Decimal Places',
        type: 'number',
        required: false,
        defaultValue: 0,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  currency: {
    type: 'currency',
    label: 'Currency',
    description: 'Currency input with formatting',
    category: 'data-entry',
    icon: DollarSign,
    htmlInputType: 'text',
    componentPath: 'data-entry/CurrencyField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'currency',
        label: 'Currency Code',
        type: 'text',
        required: false,
        defaultValue: 'USD',
        helpText: 'ISO 4217 currency code',
      },
      {
        key: 'min',
        label: 'Minimum Value',
        type: 'number',
        required: false,
      },
      {
        key: 'max',
        label: 'Maximum Value',
        type: 'number',
        required: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  range: {
    type: 'range',
    label: 'Range',
    description: 'Number range with from/to values',
    category: 'data-entry',
    icon: ArrowUpDown,
    htmlInputType: 'text',
    componentPath: 'data-entry/RangeField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'min',
        label: 'Minimum Value',
        type: 'number',
        required: false,
      },
      {
        key: 'max',
        label: 'Maximum Value',
        type: 'number',
        required: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Advanced',
  },

  date: {
    type: 'date',
    label: 'Date',
    description: 'Date picker with validation',
    category: 'data-entry',
    icon: Calendar,
    htmlInputType: 'date',
    componentPath: 'data-entry/DateField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: true,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'minDate',
        label: 'Minimum Date',
        type: 'text',
        required: false,
        helpText: 'ISO date or function (e.g., "currentDate-10years")',
      },
      {
        key: 'maxDate',
        label: 'Maximum Date',
        type: 'text',
        required: false,
        helpText: 'ISO date or function (e.g., "currentDate+2years")',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  datetime: {
    type: 'datetime',
    label: 'Date & Time',
    description: 'Date and time picker',
    category: 'data-entry',
    icon: Calendar,
    htmlInputType: 'datetime-local',
    componentPath: 'data-entry/DateTimeField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: true,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'minDateTime',
        label: 'Minimum Date/Time',
        type: 'text',
        required: false,
      },
      {
        key: 'maxDateTime',
        label: 'Maximum Date/Time',
        type: 'text',
        required: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  boolean: {
    type: 'boolean',
    label: 'Boolean',
    description: 'True/false checkbox or switch',
    category: 'data-entry',
    icon: ToggleLeft,
    htmlInputType: 'checkbox',
    componentPath: 'data-entry/BooleanField',
    capabilities: {
      supportsValidation: false,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'defaultValue',
        label: 'Default Value',
        type: 'boolean',
        required: false,
        defaultValue: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  url: {
    type: 'url',
    label: 'URL',
    description: 'URL input with validation and link opening',
    category: 'data-entry',
    icon: Link,
    htmlInputType: 'url',
    componentPath: 'data-entry/UrlField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    defaultValidationRules: {
      pattern: '^https?://.*',
      message: 'Please enter a valid URL starting with http:// or https://',
    },
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  textarea: {
    type: 'textarea',
    label: 'Text Area',
    description: 'Multi-line text input with optional rich text',
    category: 'data-entry',
    icon: FileText,
    htmlInputType: 'textarea',
    componentPath: 'data-entry/TextAreaField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: true,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'mode',
        label: 'Editor Mode',
        type: 'select',
        required: false,
        defaultValue: 'plaintext',
        options: [
          { value: 'plaintext', label: 'Plain Text' },
          { value: 'richtext', label: 'Rich Text' },
        ],
      },
      {
        key: 'minLength',
        label: 'Minimum Length',
        type: 'number',
        required: false,
      },
      {
        key: 'maxLength',
        label: 'Maximum Length',
        type: 'number',
        required: false,
      },
      {
        key: 'rows',
        label: 'Rows',
        type: 'number',
        required: false,
        defaultValue: 4,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  richtext: {
    type: 'richtext',
    label: 'Rich Text Editor',
    description: 'Rich text editor with HTML formatting support',
    category: 'data-entry',
    icon: FileText,
    htmlInputType: 'textarea',
    componentPath: 'data-entry/RichTextField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: true,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'minLength',
        label: 'Minimum Length',
        type: 'number',
        required: false,
      },
      {
        key: 'maxLength',
        label: 'Maximum Length',
        type: 'number',
        required: false,
      },
      {
        key: 'height',
        label: 'Editor Height (px)',
        type: 'number',
        required: false,
        helpText: 'Fixed height in pixels for the editor',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Advanced',
  },

  file: {
    type: 'file',
    label: 'File Upload',
    description: 'File upload with type and size restrictions',
    category: 'data-entry',
    icon: FileText,
    htmlInputType: 'file',
    componentPath: 'data-entry/FileField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: true,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'allowedTypes',
        label: 'Allowed File Types',
        type: 'select',
        required: false,
        options: [
          { value: 'documents', label: 'Documents Only' },
          { value: 'images', label: 'Images Only' },
          { value: 'both', label: 'Documents & Images' },
        ],
        defaultValue: 'both',
      },
      {
        key: 'maxFileSize',
        label: 'Max File Size (MB)',
        type: 'number',
        required: false,
        defaultValue: 10,
      },
      {
        key: 'multiple',
        label: 'Allow Multiple Files',
        type: 'boolean',
        required: false,
        defaultValue: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  document: {
    type: 'document',
    label: 'Document',
    description: 'Link to an existing document from your vault, with option to upload a new one',
    category: 'data-entry',
    icon: FileSearch,
    componentPath: 'data-entry/DocumentField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: true,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  json: {
    type: 'json',
    label: 'JSON',
    description: 'JSON editor with syntax validation',
    category: 'data-entry',
    icon: FileJson,
    htmlInputType: 'textarea',
    componentPath: 'data-entry/JsonField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    allowedInFieldDef: true,
    allowedInFormBuilder: false,
    group: 'Advanced',
    adminOnly: true,
  },

  // ========================================
  // SELECTION FIELDS
  // ========================================

  select: {
    type: 'select',
    label: 'Select',
    description: 'Single selection dropdown',
    category: 'selection',
    icon: List,
    htmlInputType: 'select',
    componentPath: 'selection/SelectField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: true,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'options',
        label: 'Options',
        type: 'textarea',
        required: true,
        helpText:
          'JSON array of {value, label} objects or comma-separated values',
      },
      {
        key: 'allowOther',
        label: 'Allow "Other" Option',
        type: 'boolean',
        required: false,
        defaultValue: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  multiselect: {
    type: 'multiselect',
    label: 'Multi-Select',
    description: 'Multiple selection dropdown',
    category: 'selection',
    icon: List,
    htmlInputType: 'select-multiple',
    componentPath: 'selection/SelectField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: true,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'options',
        label: 'Options',
        type: 'textarea',
        required: true,
        helpText: 'JSON array of {value, label} objects',
      },
      {
        key: 'maxSelections',
        label: 'Maximum Selections',
        type: 'number',
        required: false,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  country: {
    type: 'country',
    label: 'Country',
    description: 'Country selection dropdown',
    category: 'selection',
    icon: Globe,
    htmlInputType: 'select',
    componentPath: 'selection/CountryField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Geographic',
  },

  province: {
    type: 'province',
    label: 'Province/State',
    description: 'Province/state selection (cascading from country)',
    category: 'selection',
    icon: MapPin,
    htmlInputType: 'select',
    componentPath: 'selection/ProvinceField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'countryFieldId',
        label: 'Country Field ID',
        type: 'text',
        required: true,
        helpText: 'ID of the country field to cascade from',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Geographic',
  },

  city: {
    type: 'city',
    label: 'City',
    description: 'City selection (cascading from province)',
    category: 'selection',
    icon: MapPin,
    htmlInputType: 'select',
    componentPath: 'selection/CityField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'provinceFieldId',
        label: 'Province Field ID',
        type: 'text',
        required: true,
        helpText: 'ID of the province field to cascade from',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Geographic',
  },

  address: {
    type: 'address',
    label: 'Address',
    description: 'Complete address with multiple fields',
    category: 'selection',
    icon: MapPin,
    htmlInputType: 'text',
    componentPath: 'selection/AddressField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: true,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'includeCountry',
        label: 'Include Country',
        type: 'boolean',
        required: false,
        defaultValue: true,
      },
      {
        key: 'includeProvince',
        label: 'Include Province/State',
        type: 'boolean',
        required: false,
        defaultValue: true,
      },
      {
        key: 'includeCity',
        label: 'Include City',
        type: 'boolean',
        required: false,
        defaultValue: true,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Geographic',
  },

  language: {
    type: 'language',
    label: 'Language',
    description: 'Language selection dropdown',
    category: 'selection',
    icon: Globe,
    htmlInputType: 'select',
    componentPath: 'selection/LanguageField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Geographic',
  },

  // ========================================
  // LAYOUT FIELDS
  // ========================================

  spacer: {
    type: 'spacer',
    label: 'Spacer',
    description: 'Empty space or placeholder',
    category: 'layout',
    icon: Square,
    componentPath: 'layout/SpacerField',
    capabilities: {
      supportsValidation: false,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    allowedInFieldDef: false,
    allowedInFormBuilder: true,
    group: 'Layout',
  },

  field_group: {
    type: 'field_group',
    label: 'Field Group',
    description: 'Group of related fields',
    category: 'layout',
    icon: FolderOpen,
    componentPath: 'layout/FieldGroupField',
    capabilities: {
      supportsValidation: false,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: true,
      allowsChildren: true,
    },
    configOptions: [
      {
        key: 'title',
        label: 'Group Title',
        type: 'text',
        required: false,
      },
      {
        key: 'collapsible',
        label: 'Collapsible',
        type: 'boolean',
        required: false,
        defaultValue: false,
      },
    ],
    allowedInFieldDef: false,
    allowedInFormBuilder: true,
    group: 'Layout',
  },

  tab_container: {
    type: 'tab_container',
    label: 'Tab Container',
    description: 'Tabbed sections for organizing fields',
    category: 'layout',
    icon: Layout,
    componentPath: 'layout/TabContainerField',
    capabilities: {
      supportsValidation: false,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: true,
      allowsChildren: true,
    },
    configOptions: [
      {
        key: 'tabs',
        label: 'Tab Configuration',
        type: 'textarea',
        required: true,
        helpText: 'JSON array of tab definitions',
      },
    ],
    allowedInFieldDef: false,
    allowedInFormBuilder: true,
    group: 'Layout',
  },

  divider: {
    type: 'divider',
    label: 'Divider',
    description: 'Section divider with optional collapse',
    category: 'layout',
    icon: Minus,
    componentPath: 'layout/DividerField',
    capabilities: {
      supportsValidation: false,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'title',
        label: 'Section Title',
        type: 'text',
        required: false,
      },
      {
        key: 'collapsible',
        label: 'Collapsible',
        type: 'boolean',
        required: false,
        defaultValue: false,
      },
    ],
    allowedInFieldDef: false,
    allowedInFormBuilder: true,
    group: 'Layout',
  },

  dialog_button: {
    type: 'dialog_button',
    label: 'Dialog Button',
    description: 'Button that opens a dialog with fields',
    category: 'layout',
    icon: MessageSquare,
    componentPath: 'layout/DialogButtonField',
    capabilities: {
      supportsValidation: false,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: true,
      allowsChildren: true,
    },
    configOptions: [
      {
        key: 'buttonText',
        label: 'Button Text',
        type: 'text',
        required: true,
      },
      {
        key: 'dialogTitle',
        label: 'Dialog Title',
        type: 'text',
        required: false,
      },
    ],
    allowedInFieldDef: false,
    allowedInFormBuilder: true,
    group: 'Layout',
  },

  // ========================================
  // LEGACY/COMPATIBILITY FIELDS
  // ========================================

  time: {
    type: 'time',
    label: 'Time',
    description: 'Time picker',
    category: 'data-entry',
    icon: Clock,
    htmlInputType: 'time',
    componentPath: 'data-entry/TimeField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  radio: {
    type: 'radio',
    label: 'Radio Group',
    description: 'Radio button group',
    category: 'selection',
    icon: Circle,
    htmlInputType: 'radio',
    componentPath: 'selection/RadioField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: true,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'options',
        label: 'Options',
        type: 'textarea',
        required: true,
        helpText: 'JSON array of {value, label} objects',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  checkbox: {
    type: 'checkbox',
    label: 'Checkbox Group',
    description: 'Multiple checkboxes',
    category: 'selection',
    icon: CheckSquare,
    htmlInputType: 'checkbox',
    componentPath: 'selection/CheckboxField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: false,
      requiresOptions: true,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'options',
        label: 'Options',
        type: 'textarea',
        required: true,
        helpText: 'JSON array of {value, label} objects',
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Basic',
  },

  percentage: {
    type: 'percentage',
    label: 'Percentage',
    description: 'Percentage input (0-100)',
    category: 'data-entry',
    icon: Percent,
    htmlInputType: 'number',
    componentPath: 'data-entry/PercentageField',
    capabilities: {
      supportsValidation: true,
      supportsMinMax: true,
      requiresOptions: false,
      supportsDateFunctions: false,
      supportsFileUpload: false,
      supportsRichText: false,
      isContainer: false,
      allowsChildren: false,
    },
    configOptions: [
      {
        key: 'min',
        label: 'Minimum',
        type: 'number',
        required: false,
        defaultValue: 0,
      },
      {
        key: 'max',
        label: 'Maximum',
        type: 'number',
        required: false,
        defaultValue: 100,
      },
      {
        key: 'decimals',
        label: 'Decimal Places',
        type: 'number',
        required: false,
        defaultValue: 0,
      },
    ],
    allowedInFieldDef: true,
    allowedInFormBuilder: true,
    group: 'Advanced',
  },
};
